/**
 * Schritte der Abwicklung und Dokumente schreiben — lokal zuerst, wie alles.
 *
 * Wer einen Schritt abhakt, steht mit Namen dran: Der Name kommt aus der
 * abgelegten Anmeldung, nicht vom Server. Ein Dokument liegt sofort am Gerät
 * und geht mit dem nächsten Abgleich hoch, wie ein Foto.
 */

import { schrittId } from '../abwicklung.ts';
import type { Dokument, Dokumentart, Projektschritt, Schrittart } from '../typen.ts';
import { anmeldung, rufeServer } from './abgleich.ts';
import { db, neueId, speichere } from './db.ts';

async function bearbeiter(): Promise<string | null> {
  return (await anmeldung())?.benutzer.name ?? null;
}

/** Einen Schritt ändern; gibt es seine Zeile noch nicht, wird sie angelegt. */
export async function setzeSchritt(
  projektId: string,
  schritt: Schrittart,
  felder: Partial<Pick<Projektschritt, 'erledigt_am' | 'erledigt_von' | 'entfaellt' | 'erinnern_am' | 'notiz'>>,
): Promise<void> {
  const id = schrittId(projektId, schritt);
  const vorhanden = await db.projektschritte.get(id);
  if (vorhanden) {
    await speichere('projektschritte', vorhanden, { ...felder, geloescht_am: null });
    return;
  }
  const neu: Projektschritt = {
    id,
    seq: null,
    schmutzig: true,
    geaendert_am: new Date().toISOString(),
    geloescht_am: null,
    projekt_id: projektId,
    schritt,
    erledigt_am: null,
    erledigt_von: null,
    entfaellt: false,
    erinnern_am: null,
    notiz: null,
    ...felder,
  };
  await speichere('projektschritte', neu, {
    projekt_id: neu.projekt_id,
    schritt: neu.schritt,
    erledigt_am: neu.erledigt_am,
    erledigt_von: neu.erledigt_von,
    entfaellt: neu.entfaellt,
    erinnern_am: neu.erinnern_am,
    notiz: neu.notiz,
  });
}

/** Erledigt, jetzt, vom Angemeldeten. */
export async function hakeAb(projektId: string, schritt: Schrittart): Promise<void> {
  await setzeSchritt(projektId, schritt, {
    erledigt_am: new Date().toISOString(),
    erledigt_von: await bearbeiter(),
    entfaellt: false,
  });
}

/**
 * Wie `hakeAb`, aber nur, wenn noch niemand abgehakt hat — für die Stellen,
 * an denen der Schritt nebenbei passiert (Angebot versendet, Auftrag
 * angelegt, Abnahme unterschrieben). Ein schon gesetzter Zeitpunkt bleibt.
 */
export async function hakeAbFallsOffen(projektId: string, schritt: Schrittart): Promise<void> {
  const vorhanden = await db.projektschritte.get(schrittId(projektId, schritt));
  if (vorhanden?.erledigt_am && !vorhanden.geloescht_am) return;
  await hakeAb(projektId, schritt);
}

export async function legeDokumentAb(projektId: string, art: Dokumentart, datei: File): Promise<Dokument> {
  const neu: Dokument = {
    id: neueId(),
    seq: null,
    schmutzig: true,
    geaendert_am: new Date().toISOString(),
    geloescht_am: null,
    projekt_id: projektId,
    art,
    titel: datei.name.replace(/\.[^.]+$/, ''),
    dateiname: datei.name,
    mime: datei.type || 'application/octet-stream',
    groesse_bytes: datei.size,
    angelegt_von: await bearbeiter(),
    hochgeladen: false,
  };
  // Erst die Datei, dann die Zeile: Ohne Datei soll keine Zeile auf das
  // Hochladen warten, die nie etwas hochladen kann.
  await db.dokumentdateien.put({ dokument_id: neu.id, blob: datei });
  await speichere('dokumente', neu, {
    projekt_id: neu.projekt_id,
    art: neu.art,
    titel: neu.titel,
    dateiname: neu.dateiname,
    mime: neu.mime,
    groesse_bytes: neu.groesse_bytes,
    angelegt_von: neu.angelegt_von,
  });
  return neu;
}

export async function entferneDokument(dok: Dokument): Promise<void> {
  await speichere('dokumente', dok, { geloescht_am: new Date().toISOString() });
}

/** Die Datei: am Gerät, wenn sie noch dort liegt, sonst vom Server. */
export async function dokumentdatei(dok: Dokument): Promise<Blob> {
  const lokal = await db.dokumentdateien.get(dok.id);
  if (lokal) return lokal.blob;
  const antwort = await rufeServer(`/api/dokumente/${dok.id}/datei`);
  if (!antwort.ok) {
    throw new Error(navigator.onLine ? `Datei nicht geladen (${antwort.status})` : 'Keine Verbindung');
  }
  return antwort.blob();
}

/**
 * Typen, die im Fenster angezeigt werden dürfen. Eine Blob-Adresse gehört zu
 * unserer Herkunft: Ein Dokument mit „text/html“ liefe dort als Seite der
 * Anwendung und käme an die Anmeldung. Alles andere wird als Download
 * geöffnet (Sicherheitsprüfung, 11/2026; dieselbe Liste wie
 * apps/api/src/dateitypen.ts).
 */
const ANZEIGBAR = new Set(['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'image/gif', 'text/plain', 'text/csv']);

export function sichererBlob(blob: Blob): Blob {
  const typ = blob.type.split(';')[0]!.trim().toLowerCase();
  return ANZEIGBAR.has(typ) && blob.type === typ ? blob : new Blob([blob], { type: ANZEIGBAR.has(typ) ? typ : 'application/octet-stream' });
}

/**
 * Öffnen in einem neuen Fenster. Das Fenster geht sofort beim Tippen auf —
 * nach einem Abruf hielte Safari es für ein Popup.
 */
export async function oeffneDokument(dok: Dokument): Promise<void> {
  const fenster = window.open('', '_blank');
  try {
    const url = URL.createObjectURL(sichererBlob(await dokumentdatei(dok)));
    if (fenster) fenster.location.href = url;
    else window.location.href = url;
    // Freigeben, wenn das Fenster die Datei sicher geladen hat.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (fehler) {
    fenster?.close();
    throw fehler;
  }
}
